import React from 'react';
import { connect } from 'react-redux';
import { getProgressStatusLocalizationKey } from 'utils/localization';
import { isPassed, isFailed } from 'utils/comparison';
import { localize } from 'core/localization';
import { ShapedIcon } from 'components/common';
import { isOverallMasteryScore, shouldSubmitAllQuestions } from 'store/settings/selectors';
import { isAttemptSubmitted } from 'store/course/selectors';
import {
  SectionContentWrapper,
  SectionTitle,
  Footer,
  SectionProgressStatusIconWrapper,
  SectionProgressStatusWrapper,
  SectionSubText,
  ProgressBar,
  IconArrowRight
} from './SectionContent.styled';
import { RootAppState } from 'store/types';

export const LIST_ITEM_TITLE_LENGTH = 45;

type SectionContentProps = {
  progress: number;
  title: string;
  status: number;
  affectsProgress?: boolean;
  isSubmitAtOnce?: boolean;
  isCourseSubmitted?: boolean;
  affectProgressQuestionsCountInSection?: number;
  answeredSectionQuestionsCount?: number;
  correctlyAffectProgressQuestionsCountInSection?: number;
  sectionProgress?: number;
  isCourseProgress: boolean;
};

function SectionContent({
  title,
  status,
  progress,
  affectsProgress,
  isSubmitAtOnce,
  isCourseSubmitted,
  affectProgressQuestionsCountInSection,
  answeredSectionQuestionsCount = 0,
  correctlyAffectProgressQuestionsCountInSection,
  sectionProgress = 0,
  isCourseProgress
}: SectionContentProps) {
  const maxLength = LIST_ITEM_TITLE_LENGTH;
  const isSectionPassed = isPassed(status);
  const isSectionFailed = isFailed(status);
  return (
    <SectionContentWrapper>
      <SectionTitle maxLength={maxLength} appearance="h4" className="section-title">
        {title}
      </SectionTitle>
      <Footer>
        {affectsProgress && (
          <SectionProgressStatusWrapper
            isPassed={
              !!(
                (!isSubmitAtOnce && isSectionPassed) ||
                (isSubmitAtOnce && isCourseSubmitted && isSectionPassed)
              )
            }
            isOverallMasteryScore={isCourseProgress}
          >
            {isSectionPassed && !isCourseProgress && (
              <SectionProgressStatusIconWrapper
                isPassed={
                  !!(
                    (!isSubmitAtOnce && isSectionPassed) ||
                    (isSubmitAtOnce && isCourseSubmitted && isSectionPassed)
                  )
                }
              >
                <ShapedIcon
                  name="ok"
                  color="buttonTextColor"
                  shapeColor="correctColor"
                  size="small"
                />
              </SectionProgressStatusIconWrapper>
            )}
            {isSectionFailed && !isCourseProgress && (
              <ShapedIcon
                name="close"
                color="textColor"
                shapeColor="contentBodyColor"
                size="small"
              />
            )}
            <SectionSubText>
              {isSubmitAtOnce
                ? renderSubmitOnceSectionText(
                    isCourseProgress,
                    status,
                    isCourseSubmitted,
                    correctlyAffectProgressQuestionsCountInSection,
                    affectProgressQuestionsCountInSection,
                    answeredSectionQuestionsCount
                  )
                : renderSectionText(
                    isCourseProgress,
                    status,
                    correctlyAffectProgressQuestionsCountInSection,
                    affectProgressQuestionsCountInSection
                  )}
            </SectionSubText>
          </SectionProgressStatusWrapper>
        )}
      </Footer>
      <Footer className="hover-state">
        <SectionSubText>{localize('[start section]')}</SectionSubText>
        <IconArrowRight name="arrow-right" size={8} color="textColor" />
      </Footer>
      {isSubmitAtOnce && isCourseSubmitted && <ProgressBar progress={progress} opacity={1.0} />}
      {isSubmitAtOnce && !isCourseSubmitted && (
        <ProgressBar progress={sectionProgress} progressColor="textColor" opacity={0.3} />
      )}
      {!isSubmitAtOnce && <ProgressBar progress={progress} opacity={1.0} />}
    </SectionContentWrapper>
  );
}
const renderSubmitOnceSectionText = (
  isCourseProgress: boolean,
  status: number,
  isCourseSubmitted = false,
  correctlyAffectProgressQuestionsCountInSection?: number,
  affectProgressQuestionsCountInSection?: number,
  answeredSectionQuestionsCount?: number
) => {
  if (isCourseProgress) {
    if (isCourseSubmitted) {
      return localize('[x y answered correctly]', {
        countCorrectlyAnsweredQuestionsInSection: correctlyAffectProgressQuestionsCountInSection,
        countQuestionsInSection: affectProgressQuestionsCountInSection
      });
    }
    return localize('[x y questions answered]', {
      countAnsweredQuestionsInSection: answeredSectionQuestionsCount,
      countQuestionsInSection: affectProgressQuestionsCountInSection
    });
  }
  if (isCourseSubmitted) {
    return localize(getProgressStatusLocalizationKey(status));
  }
  return localize('[x y questions answered]', {
    countAnsweredQuestionsInSection: answeredSectionQuestionsCount,
    countQuestionsInSection: affectProgressQuestionsCountInSection
  });
};

const renderSectionText = (
  isCourseProgress: boolean,
  status: number,
  correctlyAffectProgressQuestionsCountInSection?: number,
  affectProgressQuestionsCountInSection?: number
) => {
  if (isCourseProgress) {
    return localize('[x y answered correctly]', {
      countCorrectlyAnsweredQuestionsInSection: correctlyAffectProgressQuestionsCountInSection,
      countQuestionsInSection: affectProgressQuestionsCountInSection
    });
  } else {
    return localize(getProgressStatusLocalizationKey(status));
  }
};

function mapStateToProps(state: RootAppState) {
  return {
    isCourseProgress: isOverallMasteryScore(state),
    isSubmitAtOnce: shouldSubmitAllQuestions(state),
    isCourseSubmitted: isAttemptSubmitted(state)
  };
}
export default connect(mapStateToProps)(SectionContent);
